import React, { useState, useEffect } from 'react';

interface Company {
  id: string;
  name: string;
  testCode: string;
  prompt: string;
  createdAt: string;
}

const TeamAdmin: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/team-admin');
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch companies');
      }
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error(error);
      alert(`加载公司列表失败: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const addCompany = async () => {
    if (newCompanyName.trim() === '') return;
    try {
      const response = await fetch('/api/team-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCompanyName }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add company');
      }
      setNewCompanyName('');
      fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error(error);
      alert(`添加公司失败: ${(error as Error).message}`);
    }
  };

  const handleSelectCompany = (company: Company) => {
    setSelectedCompany(company);
    setCurrentPrompt(company.prompt);
  };

  const handleSavePrompt = async () => {
    if (!selectedCompany) return;
    try {
      const response = await fetch('/api/team-admin', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedCompany.id, prompt: currentPrompt }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save prompt');
      }
      alert('提示词已保存！');
      // Optimistically update local state or refetch
      const updatedCompanies = companies.map((c) =>
        c.id === selectedCompany.id ? { ...c, prompt: currentPrompt } : c
      );
      setCompanies(updatedCompanies);
    } catch (error) {
      console.error(error);
      alert(`保存提示词失败: ${(error as Error).message}`);
    }
  };

  if (selectedCompany) {
    return (
      <div className="p-6 bg-gray-900 text-white min-h-screen">
        <button onClick={() => setSelectedCompany(null)} className="btn btn-sm btn-outline mb-4">
          &larr; 返回公司列表
        </button>
        <h2 className="text-3xl font-bold mb-6 text-[#F5B6C1]">{selectedCompany.name} - 团队信息管理</h2>
        
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <div className="mb-4">
            <h3 className="text-xl font-semibold">团队测评码</h3>
            <p className="text-lg text-yellow-400 font-mono p-2 bg-gray-700 rounded inline-block">{selectedCompany.testCode}</p>
            <p className="text-sm text-gray-400 mt-1">团队所有成员使用此唯一测评码进行测试。</p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-2">团队报告专属提示词</h3>
            <textarea
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              placeholder="输入用于生成该团队专属报告的提示词，例如：\n- 团队背景：这是一个初创公司的核心技术团队...\n- 团队目标：希望提升沟通效率和协作能力...\n- 报告风格：请使用活泼、鼓励性的语言..."
              className="textarea textarea-bordered w-full h-48 bg-gray-700"
            ></textarea>
            <button onClick={handleSavePrompt} className="btn bg-[#F5B6C1] text-gray-900 hover:bg-pink-300 mt-4">
              保存提示词
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <h2 className="text-3xl font-bold mb-6 text-[#F5B6C1]">团队测评管理 (34才干)</h2>
      
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
        <h3 className="text-xl font-semibold mb-4">客户公司列表</h3>
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            placeholder="输入新公司名称"
            className="input input-bordered w-full max-w-xs bg-gray-700"
          />
          <button onClick={addCompany} className="btn bg-[#F5B6C1] text-gray-900 hover:bg-pink-300">
            添加公司
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>公司名称</th>
                <th>测评码</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} className="text-center">加载中...</td></tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    <td><span className="font-mono text-yellow-400">{company.testCode}</span></td>
                    <td>{company.createdAt}</td>
                    <td>
                      <button onClick={() => handleSelectCompany(company)} className="btn btn-xs btn-outline btn-info">管理</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamAdmin;